import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  useGetCompanyQuery,
  useCreateFinanceMutation,
  useCreateFinanceDraftMutation,
  useCreateFinanceDraftFetchMutation,
  useUpdateFinanceMutation,
  useUpdateFinanceDraftDetailsMutation,
  useCreateCompanyMutation,
} from "../../../../store/Company/companyApiSlice";
import { setCurrentEditingCompany } from "../../../../store/Company/companySlice";
import { isVerified } from "../../../../utils/Helper";
import { getStatesByCountryCode } from "../../../../utils/countriesState";
import { CommonToast } from "../../../common/Toast";
import useCompanyFollowUpdate from "../../Dashboard/mycompanies/CompleteCompanyProfile/useCompanyFollowUpdate";
import {
  AUDITOR_FOLLOW,
  AUDITOR_REQUEST_TYPES,
  EMPTY_AUDITOR,
  auditorFromRecord,
  auditorPayload,
  isBlankAuditor,
  validateAuditor,
} from "./auditorFields";

/* Auditor Information's data and its save. */
export const useAuditorInformation = ({ onSaved } = {}) => {
  const dispatch = useDispatch();
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);
  const companyID = currentEditingCompany?.companyID;

  const [fetchDraft] = useCreateFinanceDraftFetchMutation();
  const [createFinance] = useCreateFinanceMutation();
  const [createFinanceDraft] = useCreateFinanceDraftMutation();
  const [updateFinance] = useUpdateFinanceMutation();
  const [updateFinanceDraftDetails] = useUpdateFinanceDraftDetailsMutation();
  const [createCompany] = useCreateCompanyMutation();
  const followUpdate = useCompanyFollowUpdate();

  const { data, refetch } = useGetCompanyQuery({ companyId: companyID, token });

  useEffect(() => {
    if (data) dispatch(setCurrentEditingCompany(data));
  }, [data, dispatch]);

  /* The live record, and the queued draft amending it if there is one. */
  const liveRecord = currentEditingCompany?.financialDetails?.[0] || null;
  const [draftRecord, setDraftRecord] = useState(null);
  const [values, setValues] = useState(EMPTY_AUDITOR);
  const [states, setStates] = useState([]);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  /* Until the draft lookup answers, seeding from the live record would show
     figures the draft has already superseded. */
  const [seeded, setSeeded] = useState(false);

  const loadDraft = useCallback(async () => {
    if (!companyID) {
      setSeeded(true);
      return;
    }
    try {
      const res = await fetchDraft({ companyID, token });
      /* Transcribed: the live effect takes only the first row. */
      setDraftRecord(res?.data?.data?.[0] || null);
    } catch {
      /* The live call is unguarded, so a failure became an unhandled
         rejection. Falls back to the live record. */
      setDraftRecord(null);
    } finally {
      setSeeded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyID, token]);

  useEffect(() => {
    loadDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyID]);

  /* Seed once the draft lookup has answered — the draft wins, as the live
     effects do by running second. */
  useEffect(() => {
    if (!seeded) return;
    const record = draftRecord || liveRecord;
    setValues(record ? auditorFromRecord(record) : EMPTY_AUDITOR);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seeded, draftRecord, liveRecord]);

  /* The state list follows the country, as the live handler does. */
  useEffect(() => {
    let cancelled = false;
    if (!values.country) {
      setStates([]);
      return undefined;
    }
    getStatesByCountryCode(values.country)
      .then((found) => {
        if (!cancelled) setStates(found || []);
      })
      /* A country with no state list is normal — the live screen falls back to
         a free-text field for exactly that. */
      .catch(() => {
        if (!cancelled) setStates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [values.country]);

  const setField = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  /* Choosing a country clears the state, as the live handler does — a state
     from the previous country is worse than none. */
  const setCountry = (code) => {
    setValues((prev) => ({ ...prev, country: code || "", state: "" }));
    setErrors((prev) => ({ ...prev, country: "" }));
  };

  /* Transcribed: a firm found only in WorldBox has no TREVEX id, so a company
     record is created first and the audit points at it. */
  const chooseFirm = async (chosen) => {
    if (!chosen) {
      setValues((prev) => ({ ...prev, firmName: "", firmCompanyID: "" }));
      return;
    }

    const name = chosen.registered_company_name || chosen.name || "";
    setValues((prev) => ({ ...prev, firmName: name }));
    setErrors((prev) => ({ ...prev, firmName: "" }));

    if (chosen.companyID) {
      setValues((prev) => ({ ...prev, firmCompanyID: chosen.companyID }));
      return;
    }
    /* A typed-in name has no WorldBox id either, and creates nothing — the
       audit then records the name alone, which is the live behaviour. */
    if (!chosen.win && !chosen.wb_win) {
      setValues((prev) => ({ ...prev, firmCompanyID: "" }));
      return;
    }

    try {
      const res = await createCompany({
        data: {
          registered_company_name: name,
          country_code: chosen.country || chosen.country_code,
          wb_win: chosen.win || chosen.wb_win,
        },
        registered_company_name: name,
        country_code: chosen.country || chosen.country_code,
        token,
      });
      setValues((prev) => ({
        ...prev,
        firmCompanyID: res?.data?.data?.companyID || "",
      }));
    } catch {
      /* Transcribed: the live code swallows this and carries on with no id
         rather than abandoning the choice. */
      setValues((prev) => ({ ...prev, firmCompanyID: "" }));
    }
  };

  const save = async () => {
    const found = validateAuditor(values);
    setErrors(found);
    if (Object.keys(found).length > 0) return false;

    setBusy(true);
    try {
      const body = auditorPayload(values, {
        /* Carried through rather than dropped — the document belongs to the
           next section and this PATCH would otherwise blank it. */
        existingStatement:
          draftRecord?.audited_financial_statement ||
          liveRecord?.audited_financial_statement ||
          "",
      });

      if (isVerified(currentEditingCompany?.verifiedCompany)) {
        if (draftRecord) {
          body.append("request_type", AUDITOR_REQUEST_TYPES.update);
          await updateFinanceDraftDetails({
            data: body,
            token,
            company_financialID: draftRecord.company_financial_draftID,
          }).unwrap();
        } else {
          body.append("current_companyID", companyID);
          /* Transcribed: a draft amending an existing live record carries its
             id so the reviewer can see what it replaces. */
          if (liveRecord?.company_financialID != null) {
            body.append("company_financialID", liveRecord.company_financialID);
          }
          body.append("request_type", AUDITOR_REQUEST_TYPES.create);
          await createFinanceDraft({ data: body, token }).unwrap();
        }

        CommonToast.success("Sent for review. The team confirms the change shortly.");
        Promise.resolve(refetch()).catch(() => {});
        loadDraft();
        onSaved?.();
        return true;
      }

      body.append("companyID", companyID);

      if (liveRecord) {
        /* Transcribed: only the direct UPDATE sends a status. */
        body.append("status", "Requested");
        await updateFinance({
          token,
          company_financialID: liveRecord.company_financialID,
          data: body,
        }).unwrap();
        await followUpdate(companyID, AUDITOR_FOLLOW.updated, token);
      } else {
        await createFinance({ data: body, token }).unwrap();
        await followUpdate(companyID, AUDITOR_FOLLOW.created, token);
      }

      CommonToast.success(
        isBlankAuditor(values)
          ? "Auditor information cleared."
          : "Auditor information saved."
      );
      Promise.resolve(refetch()).catch(() => {});
      loadDraft();
      onSaved?.();
      return true;
    } catch (error) {
      /* Every live branch has an empty catch AND a `finally` that advances to
         the next section, so a rejected save moved the viewer on as though it
         had worked. */
      CommonToast.error(
        error?.data?.detail ||
          error?.data?.message ||
          error?.message ||
          "Couldn't save the auditor information. Please try again."
      );
      return false;
    } finally {
      setBusy(false);
    }
  };

  return {
    values,
    setField,
    setCountry,
    chooseFirm,
    states,
    errors,
    busy,
    save,
    /* The design puts a status pill beside the heading; a queued draft is
       awaiting review whatever the live record says. */
    status: draftRecord?.status || liveRecord?.status || "",
  };
};

export default useAuditorInformation;
